import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug, ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    showDetails: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
  };

  private handleGoHome = () => {
    window.location.href = '/home';
  };

  private toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="max-w-lg w-full">
            {/* Error Card */}
            <div className="bg-slate-900/50 backdrop-blur-xl border border-red-500/20 rounded-2xl p-8 text-center">
              {/* Icon */}
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 mb-6">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>

              {/* Title */}
              <h2 className="text-xl font-bold text-white mb-2">
                Something went wrong
              </h2>

              {/* Description */}
              <p className="text-slate-400 mb-6">
                An unexpected error occurred. This might be a temporary issue.
                Try refreshing the page or go back to the dashboard.
              </p>

              {/* Actions */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <button
                  onClick={this.handleRetry}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl transition-all active:scale-95"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try Again
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-white font-medium rounded-xl transition-all active:scale-95 border border-white/10"
                >
                  <Home className="w-4 h-4" />
                  Go to Dashboard
                </button>
              </div>

              {/* Error Details Toggle */}
              <button
                onClick={this.toggleDetails}
                className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-300 transition-colors"
              >
                <Bug className="w-3.5 h-3.5" />
                Technical Details
                {this.state.showDetails ? (
                  <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
              </button>

              {/* Error Details */}
              {this.state.showDetails && (
                <div className="mt-4 p-4 bg-slate-950/50 rounded-xl text-left border border-white/5">
                  <div className="mb-3">
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">
                      Error Message
                    </span>
                    <pre className="mt-1 text-xs text-red-400 font-mono whitespace-pre-wrap break-words">
                      {this.state.error?.message || 'Unknown error'}
                    </pre>
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">
                        Component Stack
                      </span>
                      <pre className="mt-1 text-xs text-slate-500 font-mono whitespace-pre-wrap break-words max-h-40 overflow-auto">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Functional wrapper for hooks support
export const withErrorBoundary = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback?: ReactNode
) => {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
};

// Hook-friendly error boundary using a wrapper
interface ErrorBoundaryWrapperProps {
  children: ReactNode;
  fallbackRender?: (props: { error: Error; resetError: () => void }) => ReactNode;
}

export const ErrorBoundaryWrapper: React.FC<ErrorBoundaryWrapperProps> = ({
  children,
  fallbackRender,
}) => {
  const [key, setKey] = React.useState(0);

  const resetError = () => setKey((prev) => prev + 1);

  return (
    <ErrorBoundary
      key={key}
      fallback={
        fallbackRender ? (
          <FallbackWrapper fallbackRender={fallbackRender} resetError={resetError} />
        ) : undefined
      }
    >
      {children}
    </ErrorBoundary>
  );
};

// Helper component to capture error in fallback
class FallbackWrapper extends Component<{
  fallbackRender: (props: { error: Error; resetError: () => void }) => ReactNode;
  resetError: () => void;
}> {
  state = { error: new Error('Unknown error') };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    return this.props.fallbackRender({
      error: this.state.error,
      resetError: this.props.resetError,
    });
  }
}

export default ErrorBoundary;
